import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import Context from 'managers/Context';
import { CompanyRatesModel } from 'models';
import { getLiteral } from 'utils/getLiteral';

import { RatesAddItemsEmpty } from '../shared/RatesAddItemsFeedback';
import RatesAddItemRow from '../shared/RatesAddItemRow';
import RatesAddItemsModal from '../shared/RatesAddItemsModal';

const CompanyRatesAddModal = memo(({ companiesList, handleClose, onAddLine, open, title }) => {
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [result, setResult] = useState([]);

    const loadCompanies = useCallback((text) => {
        const query = text === '' ? 'a' : text;
        setLoading(true);
        setError(false);
        Context.domainManager.getFuzzySearch(
            'companyrates',
            '',
            query,
            '',
            '',
            '',
            (items) => {
                setResult(items || []);
                setLoading(false);
                setError(false);
            },
            () => {
                setResult([]);
                setError(true);
                setLoading(false);
            },
        );
    }, []);

    useEffect(() => {
        if (open) {
            setSearch('');
            loadCompanies('');
        }
    }, [loadCompanies, open]);

    const selectedIds = useMemo(() => {
        const ids = new Set();
        companiesList?.forEach((item) => ids.add(String(item.id)));
        return ids;
    }, [companiesList]);

    const handleAdd = useCallback(
        (element) => {
            onAddLine?.(CompanyRatesModel.fromFuzzySearch(element));
        },
        [onAddLine],
    );

    const handleSearch = useCallback(
        (text) => {
            setSearch(text);
            loadCompanies(text);
        },
        [loadCompanies],
    );

    const renderContent = () => {
        if (result.length === 0) {
            return (
                <div className="rates-add-items-modal__empty">
                    <RatesAddItemsEmpty />
                </div>
            );
        }

        return (
            <div className="rates-add-items-modal__list">
                {result.map((element, index) => {
                    const id = Number(element.Id);
                    return (
                        <RatesAddItemRow
                            key={element.Id ?? index}
                            isAdded={selectedIds.has(String(id))}
                            label={element.MatchingInfo}
                            onAction={() => handleAdd(element)}
                        />
                    );
                })}
            </div>
        );
    };

    return (
        <RatesAddItemsModal
            error={error}
            isOpen={open}
            loading={loading}
            onClose={handleClose}
            onSearch={handleSearch}
            onSearchInputChange={setSearch}
            placeholder={getLiteral('action_to_search')}
            searchValue={search}
            title={title}
        >
            {renderContent()}
        </RatesAddItemsModal>
    );
});

CompanyRatesAddModal.displayName = 'CompanyRatesAddModal';

export default CompanyRatesAddModal;
